import React from 'react';
import { Grid, Box, Typography, Button } from '@mui/material';
import NavigationIcon from '@mui/icons-material/Navigation';
import { Place } from '@/types/place';
import RatingComponent from './RatingComponent';
import FavoritesComponent from './FavoritesComponent';

interface PlaceHeaderProps {
  place: Place;
  isAuthenticated: boolean;
  userId: string | null;
  authToken: string | null;
  onRefetch: () => void;
  favorites?: Place[];
}

/**
 * Helper function to get coordinates from place (handles both structure formats)
 */
const getCoordinates = (place: Place): { latitude: number; longitude: number } | null => {
  // Backend sends coordinates as nested object: coordinates: { latitude, longitude }
  if ((place as any).coordinates?.latitude != null && (place as any).coordinates?.longitude != null) {
    return {
      latitude: (place as any).coordinates.latitude,
      longitude: (place as any).coordinates.longitude,
    };
  }
  // Fallback to flat structure: latitude, longitude (if transformed)
  if (place.latitude != null && place.longitude != null) {
    return {
      latitude: place.latitude,
      longitude: place.longitude,
    };
  }
  return null;
};

/**
 * Helper function to validate place coordinates
 */
const hasValidCoordinates = (place: Place): boolean => {
  const coordinates = getCoordinates(place);
  if (!coordinates) return false;
  
  return (
    !isNaN(coordinates.latitude) &&
    !isNaN(coordinates.longitude) &&
    (coordinates.latitude !== 0 || coordinates.longitude !== 0)
  );
};

/**
 * Helper function to generate Google Maps URL with coordinates
 */
const getGoogleMapsUrl = (lat: number, lng: number): string => {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
};

/**
 * Place header component displaying name, rating, and favorites.
 */
export const PlaceHeader: React.FC<PlaceHeaderProps> = ({
  place,
  isAuthenticated,
  userId,
  authToken,
  onRefetch,
  favorites,
}) => {
  // Use rating and rate_volume from backend response
  const averageRating = (place as any).rating ?? 0;
  const rateVolume = (place as any).rate_volume ?? 0;

  // Format address: address, city, country
  const addressParts = [];
  if ((place as any).address) addressParts.push((place as any).address);
  if (place.city) addressParts.push(place.city);
  if (place.country) addressParts.push(place.country);
  const formattedAddress = addressParts.length > 0 ? addressParts.join(', ') : null;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Place name row with favorite icon */}
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4">{place.name}</Typography>
        {isAuthenticated && userId && userId.trim() !== '' && (
          <FavoritesComponent
            placeId={place._id}
            isAuthenticated={isAuthenticated}
            userId={userId}
            authToken={authToken}
            refetchPlaceData={onRefetch}
            favorites={favorites}
          />
        )}
      </Box>
      
      {/* Address and rating row */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8} lg={8}>
          {formattedAddress && (
            <Typography variant="body2" color="text.secondary">
              {formattedAddress}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
            <RatingComponent
              placeId={place._id}
              averageRating={averageRating}
              rateVolume={rateVolume}
              isAuthenticated={isAuthenticated}
              authToken={authToken}
              refetchPlaceData={onRefetch}
            />
            {hasValidCoordinates(place) && (() => {
              const coordinates = getCoordinates(place)!;
              return (
                <Button
                  component="a"
                  href={getGoogleMapsUrl(coordinates.latitude, coordinates.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Navigate to place on Google Maps"
                  variant="outlined"
                  color="primary"
                  startIcon={<NavigationIcon />}
                >
                  Navigate
                </Button>
              );
            })()}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
